import { apiClient } from './api-client';

// ==================== WhatsApp Types ====================

export interface WhatsAppConfig {
  id: string;
  tenantId: string;
  /** Meta Graph API version, e.g. v25.0 */
  apiVersion: string;
  phoneNumberId: string;
  /** This organization's own endpoint; blank means it uses the system one. */
  apiEndpoint: string;
  /** Where sends actually go, override resolved. Read-only. */
  effectiveApiEndpoint: string;
  /** Masked - the real token never leaves the backend. */
  accessTokenMasked: string;
  hasAccessToken: boolean;
  webhookVerifyToken: string;
  /** Only used to sync templates; blank when that is not set up. */
  businessAccountId: string;
  phoneNumber: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertWhatsAppConfigDto {
  apiVersion?: string;
  phoneNumberId: string;
  /**
   * Organization's own endpoint. Omit to keep the stored value; send '' to
   * clear it and fall back to the system endpoint.
   */
  apiEndpoint?: string;
  /** Required on create; omit on update to keep the stored token. */
  accessToken?: string;
  webhookVerifyToken?: string;
  businessAccountId?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface SendWhatsAppTextDto {
  to: string;
  message: string;
  ticketId?: string;
  replyToWaMessageId?: string;
}

export interface SendWhatsAppTextResult {
  success: boolean;
  id: string;
  waMessageId?: string;
  waId?: string;
  message: string;
}


export interface WhatsAppCannedReply {
  id: string;
  title: string;
  body: string;
}

export interface WhatsAppAutoReply {
  welcomeEnabled: boolean;
  welcomeMessage: string;
  cannedReplies: WhatsAppCannedReply[];
}

export interface WhatsAppTimelineMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  type: string;
  text: string | null;
  at: string;
  /** Outbound only: pending / sent / delivered / read / failed. */
  status: string | null;
  waMessageId: string | null;
  mediaMimeType: string | null;
  /** Inbound media, fetched through the gateway proxy; null for outbound. */
  mediaId: string | null;
  /** Our stored copy of outbound media; null for inbound. */
  fileId: string | null;
  filename: string | null;
  /** True for the automatic welcome message. */
  automated: boolean;
  errorMessage: string | null;
}

export interface WhatsAppConversation {
  id: string;
  phone: string;
  name: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastDirection: 'inbound' | 'outbound' | null;
  unreadCount: number;
  messageCount: number;
  /** Whether a free-form reply is currently allowed by Meta's 24h rule. */
  isWithin24h: boolean;
  windowExpiresAt: string | null;
}

export interface WhatsAppConversationThread {
  id: string;
  phone: string;
  name: string | null;
  isWithin24h: boolean;
  windowExpiresAt: string | null;
  messages: WhatsAppTimelineMessage[];
}


export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  /** APPROVED / PENDING / REJECTED, as Meta reports it. */
  status: string;
  category: string;
  bodyText: string;
  headerText: string | null;
  footerText: string | null;
  /** How many {{n}} placeholders the body has. */
  parameterCount: number;
  headerParameterCount: number;
  /** Only APPROVED templates can actually be sent. */
  isSendable: boolean;
}

export interface SendWhatsAppMediaDto {
  to: string;
  /** Id of a file already uploaded to the file-service. */
  fileId: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

export interface SendWhatsAppTemplateDto {
  to: string;
  templateName: string;
  languageCode: string;
  parameters?: string[];
  headerParameters?: string[];
}

export interface SyncWhatsAppTemplatesResult {
  success: boolean;
  message: string;
  templates: WhatsAppTemplate[];
}

// ==================== Facebook Types ====================

export interface FacebookConfig {
  id: string;
  tenantId: string;
  apiVersion: string;
  pageId: string;
  pageName: string;
  /** Masked - the real token never leaves the backend. */
  pageAccessTokenMasked: string;
  hasPageAccessToken: boolean;
  appId: string;
  /** Whether a secret is stored; the value is never returned. */
  hasAppSecret: boolean;
  webhookVerifyToken: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertFacebookConfigDto {
  apiVersion?: string;
  pageId: string;
  pageName?: string;
  /** Required on create; omit on update to keep the stored token. */
  pageAccessToken?: string;
  appId?: string;
  appSecret?: string;
  webhookVerifyToken?: string;
  isActive?: boolean;
}

export interface FacebookCannedReply {
  id: string;
  title: string;
  body: string;
}

export interface FacebookAutoReply {
  welcomeEnabled: boolean;
  welcomeMessage: string;
  cannedReplies: FacebookCannedReply[];
}

export interface FacebookTimelineMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  type: string;
  text: string | null;
  at: string;
  status: string | null;
  fbMessageId: string | null;
  automated: boolean;
  errorMessage: string | null;
  attachments: any[];
}

export interface FacebookConversation {
  id: string;
  /** Page-scoped id of the person. */
  participantId: string;
  name: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastDirection: 'inbound' | 'outbound' | null;
  unreadCount: number;
  messageCount: number;
  /** Whether a plain reply is currently allowed by Messenger's 24h rule. */
  isWithin24h: boolean;
  windowExpiresAt: string | null;
}

export interface FacebookConversationThread {
  id: string;
  participantId: string;
  name: string | null;
  isWithin24h: boolean;
  windowExpiresAt: string | null;
  messages: FacebookTimelineMessage[];
}

export interface SendFacebookTextResult {
  success: boolean;
  id: string;
  fbMessageId?: string;
  message: string;
}

// ==================== Twitter Types ====================

export interface TwitterConfig {
  id: string;
  tenantId: string;
  accountId: string;
  accountHandle: string;
  accountName?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTwitterConfigDto {
  accountId: string;
  accountHandle: string;
  accountName?: string;
  bearerToken: string;
  apiKey: string;
  apiSecret: string;
  accessToken: string;
  accessSecret: string;
  enabled?: boolean;
}

// ==================== Instagram Types ====================

export interface InstagramConfig {
  id: string;
  tenantId: string;
  instagramAccountId: string;
  pageId: string;
  username: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInstagramConfigDto {
  instagramAccountId: string;
  pageId: string;
  username: string;
  accessToken: string;
  verifyToken?: string;
  appSecret?: string;
  enabled?: boolean;
}

// ==================== SMS Types ====================

export interface SmsConfig {
  id: string;
  tenantId: string;
  phoneNumber: string;
  messagingServiceSid?: string;
  webhookUrl?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSmsConfigDto {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  messagingServiceSid?: string;
  webhookUrl?: string;
  enabled?: boolean;
}

// ==================== WhatsApp Service ====================

export const whatsappService = {
  async getConfig(): Promise<WhatsAppConfig | null> {
    try {
      const response = await apiClient.get('/channel-gateway/whatsapp/configs');
      return response.data;
    } catch {
      return null;
    }
  },

  async createConfig(dto: UpsertWhatsAppConfigDto): Promise<WhatsAppConfig> {
    const response = await apiClient.post('/channel-gateway/whatsapp/configs', dto);
    return response.data;
  },

  async updateConfig(dto: UpsertWhatsAppConfigDto): Promise<WhatsAppConfig> {
    const response = await apiClient.put('/channel-gateway/whatsapp/configs', dto);
    return response.data;
  },

  async sendTextMessage(dto: SendWhatsAppTextDto): Promise<SendWhatsAppTextResult> {
    const response = await apiClient.post('/channel-gateway/whatsapp/messages/text', dto);
    return response.data;
  },

  async deleteConfig(): Promise<void> {
    await apiClient.delete('/channel-gateway/whatsapp/configs');
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/channel-gateway/whatsapp/configs/test');
    return response.data;
  },

  async getAutoReply(): Promise<WhatsAppAutoReply> {
    const response = await apiClient.get('/channel-gateway/whatsapp/auto-reply');
    return response.data;
  },

  async updateAutoReply(dto: Partial<WhatsAppAutoReply>): Promise<WhatsAppAutoReply> {
    const response = await apiClient.put('/channel-gateway/whatsapp/auto-reply', dto);
    return response.data;
  },

  async listConversations(): Promise<WhatsAppConversation[]> {
    const response = await apiClient.get('/channel-gateway/whatsapp/conversations');
    return response.data || [];
  },

  async getConversation(phone: string): Promise<WhatsAppConversationThread> {
    const response = await apiClient.get(
      `/channel-gateway/whatsapp/conversations/${encodeURIComponent(phone)}`,
    );
    return response.data;
  },

  async sendTemplateMessage(dto: SendWhatsAppTemplateDto): Promise<SendWhatsAppTextResult> {
    const response = await apiClient.post(
      '/channel-gateway/whatsapp/messages/template',
      dto,
    );
    return response.data;
  },

  async sendMediaMessage(dto: SendWhatsAppMediaDto): Promise<SendWhatsAppTextResult> {
    const response = await apiClient.post('/channel-gateway/whatsapp/messages/media', dto);
    return response.data;
  },

  /**
   * Inbound media as an object URL. It travels as a blob because the request
   * needs the session's Authorization header, which an <img src> cannot send.
   * Callers must revoke the URL when the element unmounts.
   */
  async getInboundMediaUrl(mediaId: string): Promise<string> {
    const response = await apiClient.get(
      `/channel-gateway/whatsapp/media/${encodeURIComponent(mediaId)}`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data as Blob);
  },

  async listTemplates(): Promise<WhatsAppTemplate[]> {
    const response = await apiClient.get('/channel-gateway/whatsapp/templates');
    return response.data || [];
  },

  async syncTemplates(): Promise<SyncWhatsAppTemplatesResult> {
    const response = await apiClient.post('/channel-gateway/whatsapp/templates/sync');
    return response.data;
  },

  async markConversationRead(phone: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(
      `/channel-gateway/whatsapp/conversations/${encodeURIComponent(phone)}/read`,
    );
    return response.data;
  },
};

// ==================== Facebook Service ====================

export const facebookService = {
  async getConfig(): Promise<FacebookConfig | null> {
    try {
      const response = await apiClient.get('/channel-gateway/facebook/configs');
      return response.data;
    } catch {
      return null;
    }
  },

  async createConfig(dto: UpsertFacebookConfigDto): Promise<FacebookConfig> {
    const response = await apiClient.post('/channel-gateway/facebook/configs', dto);
    return response.data;
  },

  async updateConfig(dto: UpsertFacebookConfigDto): Promise<FacebookConfig> {
    const response = await apiClient.put('/channel-gateway/facebook/configs', dto);
    return response.data;
  },

  async deleteConfig(): Promise<void> {
    await apiClient.delete('/channel-gateway/facebook/configs');
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/channel-gateway/facebook/configs/test');
    return response.data;
  },

  async getAutoReply(): Promise<FacebookAutoReply> {
    const response = await apiClient.get('/channel-gateway/facebook/auto-reply');
    return response.data;
  },

  async updateAutoReply(dto: Partial<FacebookAutoReply>): Promise<FacebookAutoReply> {
    const response = await apiClient.put('/channel-gateway/facebook/auto-reply', dto);
    return response.data;
  },

  async listConversations(): Promise<FacebookConversation[]> {
    const response = await apiClient.get('/channel-gateway/facebook/conversations');
    return response.data || [];
  },

  async getConversation(participantId: string): Promise<FacebookConversationThread> {
    const response = await apiClient.get(
      `/channel-gateway/facebook/conversations/${encodeURIComponent(participantId)}`,
    );
    return response.data;
  },

  async markConversationRead(
    participantId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(
      `/channel-gateway/facebook/conversations/${encodeURIComponent(participantId)}/read`,
    );
    return response.data;
  },

  async sendTextMessage(dto: {
    recipientId: string;
    message: string;
  }): Promise<SendFacebookTextResult> {
    const response = await apiClient.post(
      '/channel-gateway/facebook/messages/text',
      dto,
    );
    return response.data;
  },
};

// ==================== Twitter Service ====================

export const twitterService = {
  async getConfig(): Promise<TwitterConfig | null> {
    try {
      const response = await apiClient.get('/channel-gateway/twitter/configs');
      return response.data;
    } catch {
      return null;
    }
  },

  async createConfig(dto: CreateTwitterConfigDto): Promise<TwitterConfig> {
    const response = await apiClient.post('/channel-gateway/twitter/configs', dto);
    return response.data;
  },

  async updateConfig(dto: Partial<CreateTwitterConfigDto>): Promise<TwitterConfig> {
    const response = await apiClient.put('/channel-gateway/twitter/configs', dto);
    return response.data;
  },

  async deleteConfig(): Promise<void> {
    await apiClient.delete('/channel-gateway/twitter/configs');
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/channel-gateway/twitter/configs/test');
    return response.data;
  },
};

// ==================== Instagram Service ====================

export const instagramService = {
  async getConfig(): Promise<InstagramConfig | null> {
    try {
      const response = await apiClient.get('/channel-gateway/instagram/configs');
      return response.data;
    } catch {
      return null;
    }
  },

  async createConfig(dto: CreateInstagramConfigDto): Promise<InstagramConfig> {
    const response = await apiClient.post('/channel-gateway/instagram/configs', dto);
    return response.data;
  },

  async updateConfig(dto: Partial<CreateInstagramConfigDto>): Promise<InstagramConfig> {
    const response = await apiClient.put('/channel-gateway/instagram/configs', dto);
    return response.data;
  },

  async deleteConfig(): Promise<void> {
    await apiClient.delete('/channel-gateway/instagram/configs');
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/channel-gateway/instagram/configs/test');
    return response.data;
  },
};

// ==================== SMS Service ====================

export const smsService = {
  async getConfig(): Promise<SmsConfig | null> {
    try {
      const response = await apiClient.get('/channel-gateway/sms/configs');
      return response.data;
    } catch {
      return null;
    }
  },

  async createConfig(dto: CreateSmsConfigDto): Promise<SmsConfig> {
    const response = await apiClient.post('/channel-gateway/sms/configs', dto);
    return response.data;
  },

  async updateConfig(dto: Partial<CreateSmsConfigDto>): Promise<SmsConfig> {
    const response = await apiClient.put('/channel-gateway/sms/configs', dto);
    return response.data;
  },

  async deleteConfig(): Promise<void> {
    await apiClient.delete('/channel-gateway/sms/configs');
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/channel-gateway/sms/configs/test');
    return response.data;
  },
};

export default {
  whatsapp: whatsappService,
  facebook: facebookService,
  twitter: twitterService,
  instagram: instagramService,
  sms: smsService,
};
